'use client'

import { useSession } from 'next-auth/react'
import { usePathname } from 'next/navigation'
import { useCallback, useMemo } from 'react'

import { useForm } from '@maestro/commerce-blocks/hooks/use-form'
import { loginFormSchema } from '@maestro/commerce-blocks/schemas'
import { login } from '@maestro/commerce-blocks/server/actions/auth'
import { LoginFormData } from '@maestro/data-layer'
import { useHash } from '@pearlconvert/ui/hooks/use-hash'
import { useModals } from '@pearlconvert/ui/providers/modals/modals.context'

export const useLoginForm = () => {
  const { closeModal } = useModals()
  const { removeHashParam } = useHash()
  const { update } = useSession()

  const handleLoginSuccess = useCallback(() => {
    removeHashParam('login')
    closeModal('login')
  }, [removeHashParam, closeModal])

  const pathname = usePathname()

  const loginUser = useCallback(
    async (rawData: LoginFormData, isFirstLogin = false) => {
      const result = await login(rawData, pathname, isFirstLogin)

      if (result.success && result.shouldUpdateSession) {
        await update()
      }

      return result
    },
    [pathname, update]
  )

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, isSubmitDisabled },
    formT,
  } = useForm({
    schema: loginFormSchema,
    submitAction: loginUser,
    onSuccess: handleLoginSuccess,
  })

  const fieldErrors = useMemo(() => {
    return {
      email: errors.email,
      password: errors.password,
    }
  }, [errors])

  return {
    register,
    handleSubmit,
    errors,
    isSubmitting,
    isSubmitDisabled,
    formT,
    fieldErrors,
  }
}
